import { faAsterisk, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useResizeObserver from '@react-hook/resize-observer';
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Tooltip as ChartJSTooltip,
    Filler,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    TimeScale,
    Title,
} from 'chart.js';
import 'chartjs-adapter-moment';
import queryString from 'query-string';
import { useEffect, useRef, useState } from 'react';
import { Card, Col, Container, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { Bar, Line } from 'react-chartjs-2';
import { useLocation, useNavigate } from 'react-router-dom';
import { METRICS_FOR_REVISIT_REPORT, METRICS_TRENDS, callQuery } from '../../amplify/appsync/query';
import { isDemoUser } from '../../utils/demo';
import { getSqlDateStrFromDateStr } from '../../utils/time';
import { DateType } from '../filters/dateTypes';
import { METRICS_DIMENSION_OPTIONS, METRICS_PERIOD_OPTIONS } from '../filters/metricsFilterOptions';
import ConfirmationModal from './ConfirmationModal';
import './Metrics.css';
import { RevisitReportFilterBar } from './RevisitReportFilterBar';
import { getMetricsBarOpts, getMetricsLineOpts, metricColors } from './metricsPlotOpts';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    TimeScale,
    Filler,
    Title,
    ChartJSTooltip,
    Legend,
    PointElement,
    LineElement,
);

/**
 * Revisit dashboard that displays metrics specific for revisit score
 */
export const RevisitReport = (props: {
    user: any,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryModel = queryString.parse(location.search);

    const [filterDateOptions, setFilterDateOptions]: any = useState<any>({
        dateOptionType: DateType.Relative,
        relativeStart: 7,
        relativeStartScale: "d",
        relativeEnd: 0,
        relativeEndScale: "d",
        absoluteStart: queryModel["start"] ? new Date(Number(queryModel["start"])) : new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 7),
        absoluteEnd: queryModel["end"] ? new Date(Number(queryModel["end"])) : new Date(),
    })

    const [filterOptions, setFilterOptions]: any = useState<any>({
        "dimension": queryModel["dimension"] ? queryModel["dimension"] : 0,
        "period": queryModel["period"] ? queryModel["period"] : 1,
        "forecast_horizon": queryModel["forecast_horizon"] ? queryModel["forecast_horizon"] : 0,
        "display_options": queryModel["display_options"] ? queryModel["display_options"] : ["SHOW_FORECAST", "SHOW_FORECAST_BOUNDS", "SHOW_LAST_WEEK"],
    });

    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
    const [confirmationTime, setConfirmationTime] = useState<Date>(new Date());

    const [isLoadingMetrics, setIsLoadingMetrics] = useState<boolean>(false);
    const [dimensionValues, setDimensionValues] = useState<string[]>([]);
    const [metricNames, setMetricNames] = useState<string[]>([]);
    const [metricNameToData, setMetricNameToData] = useState<any>({});
    const [metricNameToOps, setMetricNameToOps] = useState<any>({});
    const [metricNameToDischargeData, setMetricNameToDischargeData] = useState<any>({});
    const [metricNameToDischargeOps, setMetricNameToDischargeOps] = useState<any>({});
    const [metricNameToBarDowData, setMetricNameToBarDowData] = useState<any>({});
    const [metricNameToBarHourData, setMetricNameToBarHourData] = useState<any>({});

    const chartRef = useRef<any>(null);
    const [hoverPosition, setHoverPosition] = useState<Date>();

    const [plotWidth, setPlotWidth] = useState(1024);
    const plotWidthRef = useRef(plotWidth);
    const target = useRef(null);

    useEffect(() => {
        // For optimal performance, always default to 1 day granularity if querying more 
        // than one month of data (since you wouldn't be able to visualize it anyways)
        if ((filterDateOptions.absoluteEnd.getTime() - filterDateOptions.absoluteStart.getTime()) / 1000 >= (60 * 60 * 24 * 31)) {
            setFilterOptions({
                ...filterOptions,
                "period": 2, // index for 1 day granularity
            })
        }
    }, [filterOptions, filterDateOptions])

    useEffect(() => {
        refresh();
    }, []);

    useEffect(() => {
        plotWidthRef.current = plotWidth;
    }, [plotWidth]);

    useEffect(() => {
        if (metricNameToOps && Object.keys(metricNameToOps).length > 0) {
            let localMetricNameToOps: any = {};
            for (let name of Object.keys(metricNameToOps)) {
                localMetricNameToOps[name] = {
                    ...metricNameToOps[name],
                    plugins: {
                        ...metricNameToOps[name].plugins,
                        annotation: {
                            annotations: {
                                line1: {
                                    type: 'line',
                                    xMin: hoverPosition,
                                    xMax: hoverPosition,
                                    borderColor: '#999',
                                    borderDash: [2, 2],
                                    borderWidth: 1,
                                },
                            }
                        },
                    }
                };
            }
            setMetricNameToOps(localMetricNameToOps);
        }
    }, [hoverPosition]);

    const processMetricsLineData = (metrics: any) => {

        if (!metrics || Object.keys(metrics).length === 0) {
            setMetricNameToData({});
            setMetricNameToOps({});
            return;
        }

        let data: any = {};
        let opts: any = {};
        let updatedMetricNames: string[] = ["REVISIT"];

        //
        // Format for Chart.js
        //
        for (let metricName of updatedMetricNames) {
            data[metricName] = {
                datasets: []
            };
            data[metricName].datasets.push({
                data: [],
                label: metricName,
                borderColor: metricColors(METRICS_DIMENSION_OPTIONS[0].name, 0, ''),
                backgroundColor: metricColors(METRICS_DIMENSION_OPTIONS[0].name, 0, ''),
                hoverBorderColor: "blue",
                hoverBackgroundColor: "blue",
                fill: false,
                // spanGaps: 1000 * 60 * 60 * 1, // Setting this will cause the shaded region to disappear at day granularity
                borderWidth: 1,
                hoverBorderWidth: 2,
                pointRadius: 1,
                pointHoverRadius: 2,
            });

            //
            // Add the data for the main plot
            //
            for (let metric of metrics) {
                data[metricName].datasets[0].data.push({
                    x: new Date(metric[0]),
                    y: Number(metric[1])
                })
            }

            opts[metricName] = getMetricsLineOpts(false, filterDateOptions.absoluteStart, new Date(filterDateOptions.absoluteEnd.getTime() + (1000 * 60 * 60 * 24)), chartRef, hoverPosition, setHoverPosition, setConfirmationTime, setShowConfirmationModal);
        }
        setMetricNameToData(data);
        setMetricNameToOps(opts);

        // console.log("data", data);
    }

    const processMetricsDischargeRevisitLineData = (metrics: any) => {

        if (!metrics || Object.keys(metrics).length === 0) {
            setMetricNameToDischargeData({});
            setMetricNameToDischargeOps({});
            return;
        }

        let data: any = {};
        let opts: any = {};
        let updatedMetricNames: string[] = ["REVISIT_DISCHARGES"];

        //
        // Format for Chart.js
        //
        for (let metricName of updatedMetricNames) {
            data[metricName] = {
                datasets: []
            };
            data[metricName].datasets.push({
                data: [],
                label: metricName,
                borderColor: metricColors(METRICS_DIMENSION_OPTIONS[0].name, 0, ''),
                backgroundColor: metricColors(METRICS_DIMENSION_OPTIONS[0].name, 0, ''),
                hoverBorderColor: "blue",
                hoverBackgroundColor: "blue",
                fill: false,
                // spanGaps: 1000 * 60 * 60 * 1, // Setting this will cause the shaded region to disappear at day granularity
                borderWidth: 1,
                hoverBorderWidth: 2,
                pointRadius: 1,
                pointHoverRadius: 2,
            });

            //
            // Add the data for the main plot
            //
            for (let metric of metrics) {
                if (metric[0] !== null) {
                    data[metricName].datasets[0].data.push({
                        x: new Date(metric[0]),
                        y: Number(metric[1])
                    })
                }
            }

            opts[metricName] = getMetricsLineOpts(false, filterDateOptions.absoluteStart, new Date(filterDateOptions.absoluteEnd.getTime() + (1000 * 60 * 60 * 24)), chartRef, hoverPosition, setHoverPosition, setConfirmationTime, setShowConfirmationModal);
        }
        setMetricNameToDischargeData(data);
        setMetricNameToDischargeOps(opts);

        // console.log("data", data);
    }

    function calculateAverage(numbers: number[]): number {
        if (numbers.length === 0) {
            return 0; // or return null, or any other default value
        }
    
        const sum = numbers.reduce((acc, num) => acc + num, 0);
        return sum / numbers.length;
    }

    const processMetricsBarData = (rawMetrics: any) => {

        if (!rawMetrics || Object.keys(rawMetrics).length === 0) {
            setMetricNameToBarDowData({});
            setMetricNameToBarHourData({});
            return;
        }

        let updatedMetricNames: string[] = ["REVISIT"];

        const metricsByHour: any = [];
        const metricsByDay: any = [];
        for (let i = 0; i < 24; i++) {
            metricsByHour.push([]);
        }
        for (let i = 0; i < 7; i++) {
            metricsByDay.push([]);
        }

        for (let metric of rawMetrics) {
            const dt = new Date(metric[0]);
            const score = Number(metric[1]);
            // Make sure we grab the non-UTC time
            metricsByHour[dt.getHours()].push(score);
            metricsByDay[dt.getDay()].push(score);
        }

        for (let i = 0; i < 24; i++) {
            metricsByHour[i] = calculateAverage(metricsByHour[i]);
        }

        for (let i = 0; i < 7; i++) {
            metricsByDay[i] = calculateAverage(metricsByDay[i]);
        }

        const datasetsHourLabels: string[] = [];
        for (let i = 0; i < 24; i++) {
            datasetsHourLabels.push(i.toString().padStart(2, '0') + ":00");
        }

        const dataDayOfWeek = {
            labels: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
            datasets: [
                {
                    label: "Day of Week",
                    backgroundColor: metricColors("", 0, ""),
                    data: metricsByDay,
                }
            ],
        };

        const dataHour = {
            labels: datasetsHourLabels,
            datasets:[
                {
                    label: "Hour of Day",
                    backgroundColor: metricColors("", 0, ""),
                    data: metricsByHour,
                }
            ],
        };

        //
        // Format for Chart.js
        //
        for (let metricName of updatedMetricNames) {
            let metricNameToDataByDay: any = {};
            let metricNameToDataByHour: any = {};

            metricNameToDataByDay[metricName] = dataDayOfWeek;
            metricNameToDataByHour[metricName] = dataHour;

            setMetricNameToBarDowData(metricNameToDataByDay);
            setMetricNameToBarHourData(metricNameToDataByHour);
        }
    }

    const refresh = () => {
        setIsLoadingMetrics(true);
        const promises: any = [];
        const promiseOperations: string[] = []; // Keeps track of what each promise represents
        promises.push(
            callQuery({
                operation: METRICS_FOR_REVISIT_REPORT,
                period: METRICS_PERIOD_OPTIONS[filterOptions["period"]].id,
                start: getSqlDateStrFromDateStr(filterDateOptions.absoluteStart),
                end: getSqlDateStrFromDateStr(filterDateOptions.absoluteEnd),
                summaryStart: getSqlDateStrFromDateStr(new Date(new Date().getTime() - 30 * 24 * 60 * 60 * 1000)),
                summaryEnd: getSqlDateStrFromDateStr(new Date()),
            })
        )
        promiseOperations.push(METRICS_TRENDS);

        Promise.all(promises).then((promiseOutput: any) => {
            let metricsTrendOutput: any = null;
            for (let i = 0; i < promises.length; i++) {
                if (promiseOperations[i] === METRICS_TRENDS) {
                    metricsTrendOutput = promiseOutput[i];
                }
            }
            processMetricsLineData(metricsTrendOutput["revisit"]);
            processMetricsDischargeRevisitLineData(metricsTrendOutput["revisitDischarges"]);
            processMetricsBarData(metricsTrendOutput["revisit"]);
        })
        .finally(() => {
            setIsLoadingMetrics(false);
        });
    }

    // Run on subsequent iterations page width changes
    //
    useResizeObserver(target, (entry: any) => {
        console.log("useResizeObserver called with ", entry.contentRect.width)
        let resizeTo = entry.contentRect.width - 60;
        triggerResize(resizeTo);
    })

    const triggerResize = (resizeTo: number) => {
        setPlotWidth(resizeTo);
    }

    const updateFilterOptions = (option: string, val: any) => {
        if (option === "dimension") {
            setFilterOptions({
                ...filterOptions,
                "dimension_filter_index": val,
                "dimension_filter_values": [],
                [option]: val,
            });
        } else if (option === "dimension_filter_index") {
            setFilterOptions({
                ...filterOptions,
                "dimension_filter_values": [],
                [option]: val,
            });
        } else {
            setFilterOptions({
                ...filterOptions,
                [option]: val,
            });
        }
    }

    return (
        <div ref={target}>
            <div className="metrics-sidebar" style={{overflowY: 'scroll', marginTop: isDemoUser(props.user) ? '76px' : undefined}}>
                <RevisitReportFilterBar
                    isLoadingMetrics={isLoadingMetrics}
                    filterOptions={filterOptions}
                    updateFilterOptions={updateFilterOptions}
                    filterDateOptions={filterDateOptions}
                    setFilterDateOptions={setFilterDateOptions}
                    refresh={refresh}
                    user={props.user}
                />
            </div>
            <Container fluid className="metrics-container" style={{marginTop: isDemoUser(props.user) ? '140px' : undefined}}>
                {
                    (!isLoadingMetrics && Object.keys(metricNameToData).length === 0) && (
                        <Row className="mt-2 mb-2">
                            <Col md={12}>
                                <div style={{marginTop: '20%', marginLeft: '20%', marginRight: '20%'}}>
                                    <h3>
                                        <FontAwesomeIcon icon={faAsterisk} />{' '}No Results Found
                                    </h3>
                                    <hr />
                                    <h6 style={{color: '#999999'}}>
                                        No data was found for the current parameters - try adjusting the filters.
                                    </h6>
                                </div>
                            </Col>
                        </Row>
                    )
                }
                <Row className="mt-2 mb-2">
                    <Col md={12}>
                        <Card>
                            <Card.Body>
                                <div style={{ position: 'absolute', left: '16px', top: '12px', width: '370px', fontSize: '0.9rem'}}>
                                    <strong>Revisit Score (All Active Patients)</strong>{' '}
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip>
                                                The average revisit score over the specified timeframe.
                                            </Tooltip>
                                        }
                                    >
                                        <span><FontAwesomeIcon icon={faQuestionCircle} /></span>
                                    </OverlayTrigger>
                                </div>
                                <div className="text-right" style={{ position: 'absolute', right: '16px', top: '4px', width: '820px' }}>
                                    <div className="text-right float-end plot-indicator-container">
                                        {/* Any indicator/legends go here */}
                                    </div>
                                </div>
                                <div style={{ marginTop: '24px' }}>
                                    {
                                        metricNameToData && metricNameToData["REVISIT"] && metricNameToData["REVISIT"].datasets.length > 0 && metricNameToOps && metricNameToOps["REVISIT"] && (
                                        
                                            <div>
                                                {
                                                    // @ts-ignore
                                                    <Line options={metricNameToOps["REVISIT"]} data={metricNameToData["REVISIT"]} height="240px" ref={chartRef} />
                                                }
                                            </div>
                                        )
                                    }
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                <Row className="mt-2 mb-2">
                    <Col md={12}>
                        <Card>
                            <Card.Body>
                                <div style={{ position: 'absolute', left: '16px', top: '12px', width: '370px', fontSize: '0.9rem'}}>
                                    <strong>Revisit Scores (Discharges Only)</strong>{' '}
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip>
                                                The average revisit score for discharges each hour.
                                            </Tooltip>
                                        }
                                    >
                                        <span><FontAwesomeIcon icon={faQuestionCircle} /></span>
                                    </OverlayTrigger>
                                </div>
                                <div className="text-right" style={{ position: 'absolute', right: '16px', top: '4px', width: '820px' }}>
                                    <div className="text-right float-end plot-indicator-container">
                                        {/* Any indicator/legends go here */}
                                    </div>
                                </div>
                                <div style={{ marginTop: '24px' }}>
                                    {
                                        metricNameToDischargeData && metricNameToDischargeData["REVISIT_DISCHARGES"] && metricNameToDischargeData["REVISIT_DISCHARGES"].datasets.length > 0 && metricNameToDischargeOps && metricNameToDischargeOps["REVISIT_DISCHARGES"] && (
                                        
                                            <div>
                                                {
                                                    // @ts-ignore
                                                    <Line options={metricNameToDischargeOps["REVISIT_DISCHARGES"]} data={metricNameToDischargeData["REVISIT_DISCHARGES"]} height="240px" ref={chartRef} />
                                                }
                                            </div>
                                        )
                                    }
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                <Row className="mt-2 mb-2">
                    <Col md={12}>
                        <Card>
                            <Card.Body>
                                <div style={{ position: 'absolute', left: '16px', top: '12px', width: '370px', fontSize: '0.9rem'}}>
                                    <strong>Revisit Trends (All Active Patients) - Day of Week</strong>{' '}
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip>
                                                The average revisit score based on the day of week in the specified time range in your local timezone.
                                            </Tooltip>
                                        }
                                    >
                                        <span><FontAwesomeIcon icon={faQuestionCircle} /></span>
                                    </OverlayTrigger>
                                </div>
                                <div className="text-right" style={{ position: 'absolute', right: '16px', top: '4px', width: '820px' }}>
                                    <div className="text-right float-end plot-indicator-container">
                                        {/* Any indicator/legends go here */}
                                    </div>
                                </div>
                                <div style={{ marginTop: '24px' }}>
                                    {
                                        metricNameToBarDowData && metricNameToBarDowData["REVISIT"] && metricNameToBarDowData["REVISIT"].datasets.length > 0 && (
                                            <div>
                                                {
                                                    <Bar options={getMetricsBarOpts(false, false)} height="240px" data={metricNameToBarDowData["REVISIT"]} />
                                                }
                                            </div>
                                        )
                                    }
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                <Row className="mt-2 mb-2">
                    <Col md={12}>
                        <Card>
                            <Card.Body>
                                <div style={{ position: 'absolute', left: '16px', top: '12px', width: '370px', fontSize: '0.9rem'}}>
                                    <strong>Revisit Trends (All Active Patients) - Hour of Day</strong>{' '}
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip>
                                                The average revisit score based on the hour of day in the specified time range in your local timezone
                                            </Tooltip>
                                        }
                                    >
                                        <span><FontAwesomeIcon icon={faQuestionCircle} /></span>
                                    </OverlayTrigger>
                                </div>
                                <div className="text-right" style={{ position: 'absolute', right: '16px', top: '4px', width: '820px' }}>
                                    <div className="text-right float-end plot-indicator-container">
                                        {/* Any indicator/legends go here */}
                                    </div>
                                </div>
                                <div style={{ marginTop: '24px' }}>
                                    {
                                        metricNameToBarHourData && metricNameToBarHourData["REVISIT"] && metricNameToBarHourData["REVISIT"].datasets.length > 0 && (
                                        
                                            <div>
                                                {
                                                    <Bar options={getMetricsBarOpts(false, false)} height="240px" data={metricNameToBarHourData["REVISIT"]} />
                                                }
                                            </div>
                                        )
                                    }
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

            </Container>
            <ConfirmationModal showConfirmationModal={showConfirmationModal} setShowConfirmationModal={setShowConfirmationModal} confirmTime={confirmationTime} />
        </div>
    );
};
